<script lang="ts">
    import { createAccordion } from '@melt-ui/svelte';
    import { slide } from 'svelte/transition';
    import {
        VpEqualizerIcon,
        VpCheckIcon,
        VpReturnIcon,
        VpAddIcon,
        VpSubtractIcon,
    } from '@decathlon-experience/icons/svelte';

    import Drawer from '$saga/components/Drawer.svelte';
    import Button from '$saga/components/Button.svelte';
    import Slider from '$saga/components/Slider.svelte';
    import { defaultFormValues } from '$saga/views/Search/constants';
    import { isSecondaryFiltersOpen } from '$saga/views/Search/store';
    import MultiSelectFilter from './Filters/MultiSelectFilter.svelte';
    import {
        filtersContext,
        filteredToursCount,
    } from '$saga/views/Search/store';

    const {
        elements: { content, item, trigger, root },
        helpers: { isSelected },
    } = createAccordion({
        defaultValue: undefined,
    });

    // auto submit form
    // when the user clicks outside the modal
    const autoSubmitOnOutsideClick = () => {
        const form = document.getElementById('travel-search-engine-form');
        if (form) {
            const event = new Event('submit', {
                bubbles: true,
                cancelable: true,
            });
            form.dispatchEvent(event);
        }
    };
</script>

<Drawer
    handleOnOutsideClick={autoSubmitOnOutsideClick}
    isOpen={isSecondaryFiltersOpen}
    titleLabel="Filtres secondaires"
>
    <div class="h-full" slot="triggerlink">
        <Button
            class="h-full capitalize"
            intent="secondary"
            startIcon={VpEqualizerIcon}
            type="button"
        >
            Filtres
        </Button>
    </div>

    <div
        class="flex h-full flex-col gap-m px-m py-s desktop:px-3xl desktop:py-m"
    >
        <div {...$root} class="flex h-full flex-col justify-between bg-white">
            <div class="pb-4xl">
                <!--levels-->
                <div {...$item('levels')} use:item>
                    <button
                        {...$trigger('levels')}
                        class="flex w-full items-center justify-between border-t border-solid border-border-quiet py-m"
                        use:trigger
                    >
                        <span class="flex gap-xs">
                            Niveau sportif
                            <!--if filter has changed value  -->
                            {#if $filtersContext.formValues.levels.length > 0}
                                <div class="h-xl w-fit">
                                    <div
                                        class="mb-auto size-[7px] rounded-full bg-blue-brand"
                                    ></div>
                                </div>
                            {/if}
                        </span>
                        {#if $isSelected('levels')}
                            <VpSubtractIcon size={16} />
                        {:else}
                            <VpAddIcon size={16} />
                        {/if}
                    </button>
                    {#if $isSelected('levels')}
                        <div
                            class="text-sm"
                            {...$content('levels')}
                            use:content
                            transition:slide
                        >
                            <MultiSelectFilter
                                options={$filtersContext.defaultOptions
                                    ?.levels ?? []}
                                bind:formValue={
                                    $filtersContext.formValues.levels
                                }
                            />
                        </div>
                    {/if}
                </div>
                <!--types-->
                <div {...$item('types')} use:item>
                    <button
                        {...$trigger('types')}
                        class="flex w-full items-center justify-between border-t border-solid border-border-quiet py-m"
                        use:trigger
                    >
                        <span class="flex gap-xs">
                            Type de séjour
                            <!--if filter has changed value  -->
                            {#if $filtersContext.formValues.types.length > 0}
                                <div class="h-xl w-fit">
                                    <div
                                        class="mb-auto size-[7px] rounded-full bg-blue-brand"
                                    ></div>
                                </div>
                            {/if}
                        </span>
                        {#if $isSelected('types')}
                            <VpSubtractIcon size={16} />
                        {:else}
                            <VpAddIcon size={16} />
                        {/if}
                    </button>
                    {#if $isSelected('types')}
                        <div
                            class="text-sm"
                            {...$content('types')}
                            use:content
                            transition:slide
                        >
                            <MultiSelectFilter
                                options={$filtersContext.defaultOptions
                                    ?.types ?? []}
                                bind:formValue={
                                    $filtersContext.formValues.types
                                }
                            />
                        </div>
                    {/if}
                </div>

                <!--budget-->
                <div {...$item('budget')} use:item>
                    <button
                        {...$trigger('budget')}
                        class="flex w-full items-center justify-between border-t border-solid border-border-quiet py-m"
                        use:trigger
                    >
                        <span class="flex gap-xs">
                            Budget par personne
                            <!--if filter has changed value  -->
                            {#if $filtersContext.formValues.budgetMin !== defaultFormValues.budgetMin || $filtersContext.formValues.budgetMax !== defaultFormValues.budgetMax}
                                <div class="h-xl w-fit">
                                    <div
                                        class="mb-auto size-[7px] rounded-full bg-blue-brand"
                                    ></div>
                                </div>
                            {/if}
                        </span>
                        {#if $isSelected('budget')}
                            <VpSubtractIcon size={16} />
                        {:else}
                            <VpAddIcon size={16} />
                        {/if}
                    </button>
                    {#if $isSelected('budget')}
                        <div
                            class="text-sm"
                            {...$content('budget')}
                            use:content
                            transition:slide
                        >
                            <div class="p-s">
                                <Slider
                                    label="€"
                                    bind:valueMin={
                                        $filtersContext.formValues.budgetMin
                                    }
                                    bind:valueMax={
                                        $filtersContext.formValues.budgetMax
                                    }
                                    defaultMin={$filtersContext.defaultOptions
                                        ?.budgetMin}
                                    defaultMax={$filtersContext.defaultOptions
                                        ?.budgetMax}
                                />
                            </div>
                        </div>
                    {/if}
                </div>

                <!--duration-->
                <div {...$item('duration')} use:item>
                    <button
                        {...$trigger('duration')}
                        class="flex w-full items-center justify-between border-t border-solid border-border-quiet py-m"
                        use:trigger
                    >
                        <span class="flex gap-xs">
                            Durée du séjour
                            <!--if filter has changed value  -->
                            {#if $filtersContext.formValues.durationMin !== defaultFormValues.durationMin || $filtersContext.formValues.durationMax !== defaultFormValues.durationMax}
                                <div class="h-xl w-fit">
                                    <div
                                        class="mb-auto size-[7px] rounded-full bg-blue-brand"
                                    ></div>
                                </div>
                            {/if}
                        </span>
                        {#if $isSelected('duration')}
                            <VpSubtractIcon size={16} />
                        {:else}
                            <VpAddIcon size={16} />
                        {/if}
                    </button>
                    {#if $isSelected('duration')}
                        <div
                            class="text-sm"
                            {...$content('duration')}
                            use:content
                            transition:slide
                        >
                            <div class="p-s">
                                <Slider
                                    defaultMin={$filtersContext.defaultOptions
                                        ?.durationMin}
                                    defaultMax={$filtersContext.defaultOptions
                                        ?.durationMax}
                                    label="j"
                                    bind:valueMin={
                                        $filtersContext.formValues.durationMin
                                    }
                                    bind:valueMax={
                                        $filtersContext.formValues.durationMax
                                    }
                                />
                            </div>
                        </div>
                    {/if}
                </div>

                <!--tags-->
                <div {...$item('tags')} use:item>
                    <button
                        {...$trigger('tags')}
                        class="flex w-full items-center justify-between border-b border-t border-solid border-border-quiet py-m"
                        use:trigger
                    >
                        <span class="flex gap-xs">
                            Thème
                            <!--if filter has changed value  -->
                            {#if $filtersContext.formValues.tags.length > 0}
                                <div class="h-xl w-fit">
                                    <div
                                        class="mb-auto size-[7px] rounded-full bg-blue-brand"
                                    ></div>
                                </div>
                            {/if}
                        </span>
                        {#if $isSelected('tags')}
                            <VpSubtractIcon size={16} />
                        {:else}
                            <VpAddIcon size={16} />
                        {/if}
                    </button>
                    {#if $isSelected('tags')}
                        <div
                            class="text-sm"
                            {...$content('tags')}
                            use:content
                            transition:slide
                        >
                            <MultiSelectFilter
                                options={$filtersContext.defaultOptions?.tags ??
                                    []}
                                bind:formValue={$filtersContext.formValues.tags}
                            />
                        </div>
                    {/if}
                </div>
            </div>
            <div
                class="sticky bottom-0 flex w-full justify-between border-t border-solid border-border-quiet bg-white py-s"
            >
                <Button
                    intent="secondary"
                    on:click={() => {
                        $filtersContext.formValues = {
                            ...$filtersContext.formValues,
                            levels: [],
                            types: [],
                            durationMin: defaultFormValues.durationMin,
                            durationMax: defaultFormValues.durationMax,
                            budgetMin: defaultFormValues.budgetMin,
                            budgetMax: defaultFormValues.budgetMax,
                            tags: [],
                        };
                    }}
                    shape="rounded"
                    size="small"
                    startIcon={VpReturnIcon}
                    type="button"
                    >Tout effacer
                </Button>
                <Button
                    on:click={() => {
                        $isSecondaryFiltersOpen = false;
                        $filtersContext.isSubmitting = true;
                        // auto submit form
                        // when the user clicks outside the modal
                        const form = document.getElementById(
                            'travel-search-engine-form',
                        );
                        if (form) {
                            setTimeout(() => {
                                form.dispatchEvent(
                                    new Event('submit', {
                                        bubbles: true,
                                        cancelable: true,
                                    }),
                                );
                            }, 0);
                        }
                    }}
                    shape="rounded"
                    size="small"
                    startIcon={VpCheckIcon}
                    type="button"
                >
                    Appliquer {#if $filteredToursCount !== null}
                        ({$filteredToursCount}){/if}
                </Button>
            </div>
        </div>
    </div>
</Drawer>
